import React from 'react';
import { List, Show, useListContext, useShowContext, useRefresh, useDataProvider } from 'react-admin';
import { useParams } from 'react-router-dom';
import { useBreadcrumbContext } from '../components/layout/AppBreadcrumb';
import { MessageSquare, Users, RefreshCw, Trash2, ExternalLink } from 'lucide-react';
import { SearchIntegration } from '../components/common/SearchIntegration';
import { ReactAdminPagination } from '../components/ui/react-admin-pagination';
import { ListScreen } from '../components/ui/list-screen';
import { SegmentedTabs } from '../components/ui/segmented-tabs';
import { DataTable, DataColumn } from '../components/ui/data-table';
import { ConfirmDialog } from '../components/ui/confirm-dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';
import { Avatar, StatusBadge } from '../components/ui/data-cells';
import { toastSuccess, toastError } from '../lib/toast';
import { cn } from '../lib/utils';
import { CustomDataProvider } from '../types/dataProvider';
import { logger, LogCategory } from '../utils/logger';

/* ============================================================
   Session chats — Latio design system (derived). List + transcript detail
   with include-deleted toggle and reason-based message moderation.
   ============================================================ */

interface SessionChatListItem {
  id: number;
  session_id: number;
  session_title?: string;
  session_type: 'group_class' | 'private_session' | 'open_play';
  session_status: 'open' | 'full' | 'completed' | 'cancelled';
  trainer_name: string;
  trainer_id: number;
  total_messages: number;
  messages_today: number;
  messages_this_week: number;
  last_message_content?: string;
  last_message_user?: string;
  last_message_at?: string;
  has_recent_activity: boolean;
  active_participants: number;
}

interface SessionChatMessage {
  id: number;
  message?: string;
  message_content?: string;
  message_type: 'text' | 'system' | 'announcement' | 'timeline_event';
  message_metadata?: string | any;
  sender_name?: string;
  user_name?: string;
  sender_username?: string;
  user_username?: string;
  sender_profile_picture_url?: string;
  user_profile_picture_url?: string;
  created_at: string;
  is_deleted?: boolean;
}

const TYPE_LABEL: Record<string, string> = {
  group_class: 'Group class',
  private_session: 'Private session',
  open_play: 'Open play',
};
const typeLabel = (t?: string) => (t ? TYPE_LABEL[t] || t.replace(/_/g, ' ') : '—');
const fmtDateTime = (v?: string | null) =>
  v ? new Date(v).toLocaleString('en-US', { month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—';

/* ---- List ---- */
const SC_TABS = [
  { value: 'all', label: 'All' },
  { value: 'group_class', label: 'Group' },
  { value: 'private_session', label: 'Private' },
  { value: 'open_play', label: 'Open play' },
];

const SessionTypeTabs = () => {
  const { filterValues, setFilters, displayedFilters } = useListContext();
  const current = (filterValues.session_type as string) || 'all';
  return (
    <SegmentedTabs
      tabs={SC_TABS}
      value={current}
      onChange={(v) => setFilters({ ...filterValues, session_type: v === 'all' ? undefined : v }, displayedFilters)}
    />
  );
};

const SessionChatsListBody = () => {
  const columns: DataColumn<SessionChatListItem>[] = [
    { key: 'session_id', header: 'ID', sortable: true, width: '1px', cell: (r) => <span className="tabular-nums text-ink-2">#{r.session_id}</span> },
    {
      key: 'session',
      header: 'Session',
      cell: (r) => (
        <div className="min-w-0">
          <div className="truncate font-semibold text-ink">{r.session_title || `Session #${r.session_id}`}</div>
          <div className="truncate text-[13px] text-ink-3">{r.trainer_name}</div>
        </div>
      ),
    },
    { key: 'type', header: 'Type', cell: (r) => <span className="text-ink-2">{typeLabel(r.session_type)}</span> },
    { key: 'status', header: 'Status', cell: (r) => <StatusBadge status={r.session_status} /> },
    {
      key: 'participants',
      header: 'Active',
      cell: (r) => (
        <span className="inline-flex items-center gap-1.5 text-ink-2">
          <Users className="size-3.5" />
          {r.active_participants || 0}
        </span>
      ),
    },
    {
      key: 'messages',
      header: 'Messages',
      cell: (r) => (
        <div>
          <div className="inline-flex items-center gap-1.5 text-ink">
            <MessageSquare className="size-3.5 text-ink-2" />
            {r.total_messages || 0}
          </div>
          <div className="text-[12px] text-ink-3">Today {r.messages_today || 0} · Week {r.messages_this_week || 0}</div>
        </div>
      ),
    },
    {
      key: 'last',
      header: 'Last message',
      cell: (r) => (
        <div className="max-w-[260px]">
          <div className="truncate text-ink">{r.last_message_content || <span className="text-ink-3">No messages</span>}</div>
          {r.last_message_user && <div className="truncate text-[12px] text-ink-3">by {r.last_message_user}</div>}
        </div>
      ),
    },
    { key: 'last_message_at', header: 'Activity', sortable: true, cell: (r) => fmtDateTime(r.last_message_at) },
  ];
  return (
    <ListScreen tabs={<SessionTypeTabs />} tokens={null} count={null}>
      <DataTable columns={columns} rowClick="show" emptyMessage="No session chats found." />
      <div className="pt-1">
        <ReactAdminPagination />
      </div>
    </ListScreen>
  );
};

export const SessionChatList = (props: any) => (
  <List {...props} actions={false} empty={false} pagination={false} sort={{ field: 'last_message_at', order: 'DESC' }} perPage={25} component="div">
    <SearchIntegration filterKey="q" />
    <SessionChatsListBody />
  </List>
);

/* ---- Message bubble ---- */
function ChatMessageRow({ message, onDelete }: { message: SessionChatMessage; onDelete: (id: number) => void }) {
  const isSystem = message.message_type === 'system';
  const isAnnouncement = message.message_type === 'announcement';
  const isTimeline = message.message_type === 'timeline_event';
  const centered = isSystem || isAnnouncement || isTimeline;
  const content = message.message || message.message_content || '';
  const name = message.sender_name || message.user_name || 'Unknown';
  const username = message.sender_username || message.user_username;
  const avatar = message.sender_profile_picture_url || message.user_profile_picture_url;

  let meta: any = null;
  if (isTimeline && message.message_metadata) {
    try {
      meta = typeof message.message_metadata === 'string' ? JSON.parse(message.message_metadata) : message.message_metadata;
    } catch (e) {
      logger.error(LogCategory.UI, 'Failed to parse timeline metadata', e, 'SessionChats');
    }
  }

  if (centered) {
    return (
      <div className="my-3 flex flex-col items-center gap-1">
        <span className={cn('rounded-pill px-3 py-1 text-[13px]', isAnnouncement ? 'bg-indigo-tint text-indigo' : 'bg-surface-2 text-ink-2')}>
          {message.is_deleted ? 'This message was deleted' : content}
        </span>
        {meta && (
          <div className="flex flex-wrap items-center justify-center gap-2 text-[12px] text-ink-3">
            {meta.date_time && <span>📅 {new Date(meta.date_time).toLocaleString()}</span>}
            {meta.sport_key && <span>⚽ {meta.sport_key}</span>}
            {meta.max_participants && <span>👥 Max {meta.max_participants}</span>}
            {meta.price_per_person && <span>💰 {meta.price_per_person}</span>}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="group flex gap-3 py-2">
      <Avatar src={avatar} name={name} size={32} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold text-ink">{name}</span>
          {username && <span className="text-[12px] text-ink-3">@{username}</span>}
          <span className="text-[12px] text-ink-3">{new Date(message.created_at).toLocaleString()}</span>
          {message.is_deleted && <Badge variant="secondary" className="text-[11px]">Deleted</Badge>}
        </div>
        <p className={cn('mt-0.5 text-sm', message.is_deleted ? 'italic text-ink-3' : 'text-ink')}>
          {message.is_deleted ? 'This message was deleted' : content}
        </p>
      </div>
      {!message.is_deleted && (
        <button
          type="button"
          onClick={() => onDelete(message.id)}
          title="Delete message"
          className="invisible inline-flex size-7 shrink-0 items-center justify-center rounded-pill text-ink-2 hover:bg-error-bg hover:text-error group-hover:visible"
        >
          <Trash2 className="size-3.5" />
        </button>
      )}
    </div>
  );
}

/* ---- Detail ---- */
function DetailCard({ title, icon: Icon, action, children }: { title?: string; icon?: any; action?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="rounded-card border border-hairline bg-background p-5">
      {(title || action) && (
        <div className="mb-3 flex items-center justify-between gap-3">
          <h3 className="flex items-center gap-2 text-[15px] font-bold text-ink">
            {Icon && <Icon className="size-4 text-ink-2" />}
            {title}
          </h3>
          {action}
        </div>
      )}
      {children}
    </div>
  );
}
function Fact({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="py-1">
      <div className="text-[11.5px] font-semibold uppercase tracking-[0.06em] text-ink-3">{label}</div>
      <div className="mt-0.5 text-sm text-ink">{value}</div>
    </div>
  );
}

const SessionChatShowContent = () => {
  const { record, isLoading } = useShowContext<SessionChatListItem>();
  const { id: sessionId } = useParams();
  const refresh = useRefresh();
  const dataProvider = useDataProvider<CustomDataProvider>();
  const { setData } = useBreadcrumbContext();

  const [messages, setMessages] = React.useState<SessionChatMessage[]>([]);
  const [messagesLoading, setMessagesLoading] = React.useState(true);
  const [includeDeleted, setIncludeDeleted] = React.useState(false);
  const [deleteId, setDeleteId] = React.useState<number | null>(null);

  React.useEffect(() => {
    if (record) {
      setData({ resourceId: record.session_id.toString(), resourceName: record.session_title || `Session chat #${record.session_id}`, resourceType: 'session-chat' });
    }
  }, [record, setData]);

  const fetchMessages = React.useCallback(async () => {
    if (!sessionId) return;
    setMessagesLoading(true);
    try {
      const { data } = await dataProvider.getSessionChatMessages({ sessionId, includeDeleted });
      setMessages(data?.messages || []);
    } catch (error) {
      logger.error(LogCategory.API, 'Failed to fetch session messages', error, 'SessionChats');
      setMessages([]);
    } finally {
      setMessagesLoading(false);
    }
  }, [sessionId, includeDeleted, dataProvider]);

  React.useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  const handleDelete = async (reason?: string) => {
    if (deleteId === null) return;
    try {
      await dataProvider.actionSessionChatMessage({ messageId: deleteId, action: 'delete', reason });
      toastSuccess('Message deleted');
      fetchMessages();
      refresh();
    } catch {
      toastError('Failed to delete message');
    }
  };

  if (isLoading) return <div className="p-6 text-center text-ink-2">Loading session chat…</div>;
  if (!record) return <div className="p-6 text-center text-ink-2">Session chat not found</div>;

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-4">
          <span className="flex size-[60px] items-center justify-center rounded-card bg-indigo-tint text-indigo">
            <MessageSquare className="size-7" />
          </span>
          <div>
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-[24px] font-bold leading-none text-ink" style={{ letterSpacing: '-0.02em' }}>
                {record.session_title || `Session chat #${record.session_id}`}
              </h1>
              <StatusBadge status={record.session_status} />
            </div>
            <p className="mt-1 text-sm text-ink-2">{typeLabel(record.session_type)} · {record.trainer_name}</p>
          </div>
        </div>
        <a
          href={`/#/sessions/${record.session_id}/show`}
          className="inline-flex h-9 items-center gap-2 rounded-pill border border-hairline px-4 text-sm font-semibold text-ink hover:bg-surface-2"
        >
          <ExternalLink className="size-4" />
          View session
        </a>
      </div>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <DetailCard><Fact label="Type" value={typeLabel(record.session_type)} /></DetailCard>
        <DetailCard><Fact label="Trainer" value={record.trainer_name} /></DetailCard>
        <DetailCard><Fact label="Messages" value={record.total_messages || 0} /></DetailCard>
        <DetailCard><Fact label="Participants" value={record.active_participants || 0} /></DetailCard>
      </div>

      <DetailCard
        title="Conversation"
        icon={MessageSquare}
        action={
          <div className="flex items-center gap-3">
            <label className="flex cursor-pointer items-center gap-2 text-[13px] text-ink-2">
              <input type="checkbox" checked={includeDeleted} onChange={(e) => setIncludeDeleted(e.target.checked)} className="rounded" />
              Show deleted
            </label>
            <button
              type="button"
              onClick={fetchMessages}
              disabled={messagesLoading}
              className="inline-flex h-8 items-center gap-1.5 rounded-pill border border-hairline px-3 text-[13px] font-semibold text-ink-2 hover:text-ink disabled:opacity-50"
            >
              <RefreshCw className="size-3.5" />
              Refresh
            </button>
          </div>
        }
      >
        <ScrollArea className="h-[560px] pr-2">
          {messagesLoading ? (
            <p className="py-8 text-center text-sm text-ink-2">Loading messages…</p>
          ) : messages.length > 0 ? (
            <div className="divide-y divide-hairline">
              {messages.map((m) => (
                <ChatMessageRow key={m.id} message={m} onDelete={(mid) => setDeleteId(mid)} />
              ))}
            </div>
          ) : (
            <p className="py-8 text-center text-sm text-ink-2">No messages in this session chat yet</p>
          )}
        </ScrollArea>
      </DetailCard>

      <ConfirmDialog
        open={deleteId !== null}
        onOpenChange={(o) => !o && setDeleteId(null)}
        tone="destructive"
        title="Delete this message?"
        consequence="The message is permanently removed from the session chat."
        confirmLabel="Delete message"
        reasonRequired
        reasonChips={['Inappropriate content', 'Spam', 'Harassment']}
        onConfirm={handleDelete}
      />
    </div>
  );
};

export const SessionChatShow = (props: any) => (
  <Show {...props} actions={false} title=" " component="div">
    <SessionChatShowContent />
  </Show>
);
